import { useFormik } from "formik";
import * as Yup from "yup";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import InputText from "../../components/InputText";
import { Checkbox } from 'primereact/checkbox';
import { useRef, useState, useEffect } from "react";
import { addLocale } from 'primereact/api';
import axiosInstance from "../../utils/axiosInstance";
import PropTypes from 'prop-types';
import StateSelector from "../../components/StateSelector";
import GuarantorForm from "./GuarantorForm";
import { ilceler, iller } from "../../assets/common";
import { useLoading } from "../../components/Loading/LoadingProvider";
import { useConfirmDialog } from "../../utils/ConfirmDialog/ConfirmDialogProvider";
import WarningArea from "../../components/WarningArea";

const AddCustomer = ({ onClose, customerData, onKefilPanelToggle }) => {
    const toast = useRef(null);
    const { setLoading } = useLoading();
    const [showGuarantorPanel, setShowGuarantorPanel] = useState(false);
    const [guarantors, setGuarantors] = useState([]);
    const [deletedGuarantorIds, setDeletedGuarantorIds] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const { showDialog } = useConfirmDialog();

    // Mobil cihaz kontrolü
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    addLocale('tr', {
        firstDayOfWeek: 1,
        dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
        dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
        dayNamesMin: ['Pa', 'Pz', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
        monthNames: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
        monthNamesShort: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
        today: 'Bugün',
        clear: 'Temizle'
    });

    const isBusinessCustomer = customerData && customerData.isBusinessCustomer
        ? customerData.isBusinessCustomer === 1 ||
        customerData.isBusinessCustomer === true ||
        customerData.isBusinessCustomer?.value === "Evet" ||
        customerData.isBusinessCustomer?.id === 1
        : false;

    const initialValues = {
        name: customerData?.name || "",
        surname: customerData?.surname || "",
        identityNumber: customerData?.identityNumber || "",
        phoneNumber1: customerData?.phoneNumber1 || "",
        phoneNumber2: customerData?.phoneNumber2 || "",
        countryOfAddress: customerData ? iller.find(item => item.il === customerData.countryOfAddress) || null : null,
        districtOfAddress: customerData ? ilceler.find(item => item.il === customerData.countryOfAddress && item.ilce === customerData.districtOfAddress) || null : null,
        detailOfAddress: customerData?.detailOfAddress || "",
        countryOfWorkAddress: customerData ? iller.find(item => item.il === customerData.countryOfWorkAddress) || null : null,
        districtOfWorkAddress: customerData ? ilceler.find(item => item.il === customerData.countryOfWorkAddress && item.ilce === customerData.districtOfWorkAddress) || null : null,
        detailOfWorkAddress: customerData?.detailOfWorkAddress || "",
        birthday: customerData?.birthday ? new Date(customerData.birthday) : null,
        countryOfBirth: customerData ? iller.find(item => item.il === customerData.countryOfBirth) || null : null,
        districtOfBirth: customerData ? ilceler.find(item => item.il === customerData.countryOfBirth && item.ilce === customerData.districtOfBirth) || null : null,
        isBusinessCustomer: isBusinessCustomer,
        vkn: customerData?.vkn || "",
        companyName: customerData?.companyName || "",
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("İsim alanı zorunludur."),
        surname: Yup.string().required("Soyisim alanı zorunludur."),
        identityNumber: Yup.string()
            .matches(/^[0-9]{11}$/, "T.C kimlik numarası 11 haneli olmalıdır.")
            .required("T.C kimlik numarası zorunludur."),
        phoneNumber1: Yup.string()
            .test('phone-format', 'Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır', function (value) {
                if (!value) return false;
                const cleanPhone = value.replace(/[^\d]/g, '');
                const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
                return /^5[0-9]{9}$/.test(phoneWithoutCountry);
            })
            .required("Telefon numarası zorunludur."),
        phoneNumber2: Yup.string()
            .test('phone-format-optional', 'Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır', function (value) {
                if (!value || value === '') return true;
                const cleanPhone = value.replace(/[^\d]/g, '');
                const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
                return /^5[0-9]{9}$/.test(phoneWithoutCountry);
            }),
        countryOfAddress: Yup.object().nullable().required("İkametgah adres il zorunludur."),
        districtOfAddress: Yup.object().nullable().required("İkametgah adres ilçe zorunludur."),
        detailOfAddress: Yup.string().required("İkametgah adres detayı zorunludur."),
        countryOfWorkAddress: Yup.object().nullable().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari müşteriler için iş yeri il zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        districtOfWorkAddress: Yup.object().nullable().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari müşteriler için iş yeri ilçe zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        detailOfWorkAddress: Yup.string().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari müşteriler için iş yeri adresi zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        countryOfBirth: Yup.object().nullable().required("Doğum yeri il zorunludur."),
        districtOfBirth: Yup.object().nullable().required("Doğum yeri ilçe zorunludur."),
        birthday: Yup.mixed()
            .test('is-date', 'Geçersiz tarih formatı', function (value) {
                if (!value) return false;
                if (value instanceof Date && !isNaN(value.getTime())) return true;
                return false;
            })
            .required("Doğum tarihi zorunludur."),
        isBusinessCustomer: Yup.boolean(),
        vkn: Yup.string().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari müşteriler için VKN zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        companyName: Yup.string().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari müşteriler için firma ünvanı zorunludur."),
            otherwise: schema => schema.nullable()
        }),
    });

    const guarantorValidationSchema = Yup.object().shape({
        name: Yup.string().required("İsim alanı zorunludur."),
        surname: Yup.string().required("Soyisim alanı zorunludur."),
        identityNumber: Yup.string()
            .matches(/^[0-9]{11}$/, "T.C kimlik numarası 11 haneli olmalıdır.")
            .required("T.C kimlik numarası zorunludur."),
        phoneNumber1: Yup.string()
            .test('phone-format', 'Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır', function (value) {
                if (!value) return false;
                const cleanPhone = value.replace(/[^\d]/g, '');
                const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
                return /^5[0-9]{9}$/.test(phoneWithoutCountry);
            })
            .required("Telefon numarası zorunludur."),
        phoneNumber2: Yup.string()
            .test('phone-format-optional', 'Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır', function (value) {
                if (!value || value === '') return true;
                const cleanPhone = value.replace(/[^\d]/g, '');
                const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
                return /^5[0-9]{9}$/.test(phoneWithoutCountry);
            }),
        countryOfAddress: Yup.object().nullable().required("İkametgah adres il zorunludur."),
        districtOfAddress: Yup.object().nullable().required("İkametgah adres ilçe zorunludur."),
        detailOfAddress: Yup.string().required("İkametgah adres detayı zorunludur."),
        countryOfBirth: Yup.object().nullable().required("Doğum yeri il zorunludur."),
        districtOfBirth: Yup.object().nullable().required("Doğum yeri ilçe zorunludur."),
        birthday: Yup.mixed()
            .test('is-date', 'Geçersiz tarih formatı', function (value) {
                if (!value) return false;
                if (value instanceof Date && !isNaN(value.getTime())) return true;
                return false;
            })
            .required("Doğum tarihi zorunludur."),
        isBusinessCustomer: Yup.boolean(),
        vkn: Yup.string().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari kefiller için VKN zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        companyName: Yup.string().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari kefiller için firma ünvanı zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        countryOfWorkAddress: Yup.object().nullable().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari kefiller için iş yeri il zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        districtOfWorkAddress: Yup.object().nullable().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari kefiller için iş yeri ilçe zorunludur."),
            otherwise: schema => schema.nullable()
        }),
        detailOfWorkAddress: Yup.string().when("isBusinessCustomer", {
            is: true,
            then: schema => schema.required("Ticari kefiller için iş yeri adresi zorunludur."),
            otherwise: schema => schema.nullable()
        }),
    });

    useEffect(() => {
        if (customerData?.guarantors?.length > 0) {
            const initialGuarantors = customerData.guarantors.map(guarantor => ({
                id: guarantor.id,
                name: guarantor.name || "",
                surname: guarantor.surname || "",
                identityNumber: guarantor.identityNumber || "",
                phoneNumber1: guarantor.phoneNumber1 || "",
                phoneNumber2: guarantor.phoneNumber2 || "",
                countryOfAddress: iller.find(item => item.il === guarantor.countryOfAddress) || null,
                districtOfAddress: ilceler.find(item => item.il === guarantor.countryOfAddress && item.ilce === guarantor.districtOfAddress) || null,
                detailOfAddress: guarantor.detailOfAddress || "",
                countryOfWorkAddress: iller.find(item => item.il === guarantor.countryOfWorkAddress) || null,
                districtOfWorkAddress: ilceler.find(item => item.il === guarantor.countryOfWorkAddress && item.ilce === guarantor.districtOfWorkAddress) || null,
                detailOfWorkAddress: guarantor.detailOfWorkAddress || "",
                birthday: guarantor.birthday ? new Date(guarantor.birthday) : null,
                countryOfBirth: iller.find(item => item.il === guarantor.countryOfBirth) || null,
                districtOfBirth: ilceler.find(item => item.il === guarantor.countryOfBirth && item.ilce === guarantor.districtOfBirth) || null,
                isBusinessCustomer: guarantor.isBusinessCustomer === 1 ||
                    guarantor.isBusinessCustomer === true ||
                    (guarantor.isBusinessCustomer?.value === "Evet" ||
                        guarantor.isBusinessCustomer?.id === 1),
                vkn: guarantor.vkn || "",
                companyName: guarantor.companyName || "",
            }));
            setGuarantors(initialGuarantors);
            setShowGuarantorPanel(initialGuarantors.length > 0);

            if (JSON.stringify(guarantors) !== JSON.stringify(initialGuarantors)) {
                setGuarantors(initialGuarantors);
                setShowGuarantorPanel(true);

                setDeletedGuarantorIds([]);
            }
        } else {
            if (guarantors.length > 0 && guarantors[0]?.id) {
                setGuarantors([]);
                setDeletedGuarantorIds([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerData]);


    const hasAnyData = (guarantor) => {
        if (!guarantor) return false;

        return Boolean(
            guarantor.name?.trim() ||
            guarantor.surname?.trim() ||
            guarantor.identityNumber?.trim() ||
            guarantor.phoneNumber1?.trim() ||
            guarantor.countryOfAddress ||
            guarantor.countryOfBirth ||
            guarantor.birthday
        );
    };

    const handleGuarantorsChange = (updatedGuarantors, deletedIds, validateFunc) => {
        if (JSON.stringify(guarantors) !== JSON.stringify(updatedGuarantors)) {
            setGuarantors(updatedGuarantors);
        }

        if (deletedIds && deletedIds.length > 0) {
            const currentIds = new Set(deletedGuarantorIds);
            const hasNewDeletedIds = deletedIds.some(id => !currentIds.has(id));

            if (hasNewDeletedIds) {
                setDeletedGuarantorIds([...new Set([...deletedGuarantorIds, ...deletedIds])]);
            }
        }

        if (validateFunc) {
            formik.setFieldValue('validateGuarantors', validateFunc, false);
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            setLoading(true); // Global loading başlat
            try {
                const hasValidGuarantors = guarantors.some(kefil => hasAnyData(kefil));

                if (hasValidGuarantors) {
                    const validateGuarantors = values.validateGuarantors;
                    if (validateGuarantors && typeof validateGuarantors === 'function') {
                        const guarantorsValid = await validateGuarantors();
                        if (!guarantorsValid) {
                            toast.current.show({
                                severity: "error",
                                summary: "Hata",
                                detail: "Kefil bilgilerinde eksiklikler var. Lütfen kontrol ediniz.",
                                life: 3000,
                            });
                            setSubmitting(false);
                            return;
                        }
                    }
                }

                // Telefon numaralarını temizle (Register komponentindeki gibi)
                const cleanPhone1 = values.phoneNumber1.replace(/[^\d]/g, '');
                const phone1 = cleanPhone1.startsWith('0') ? cleanPhone1.slice(1) : cleanPhone1;

                const cleanPhone2 = values.phoneNumber2 ? values.phoneNumber2.replace(/[^\d]/g, '') : null;
                const phone2 = cleanPhone2 ? (cleanPhone2.startsWith('0') ? cleanPhone2.slice(1) : cleanPhone2) : null;

                const customerPayload = {
                    ...customerData,
                    ...values,
                    phoneNumber1: phone1,
                    phoneNumber2: phone2,
                    isBusinessCustomer: values.isBusinessCustomer ? 1 : 0,
                    countryOfAddress: values.countryOfAddress?.il,
                    districtOfAddress: values.districtOfAddress?.ilce,
                    countryOfWorkAddress: values.countryOfWorkAddress?.il || null,
                    districtOfWorkAddress: values.districtOfWorkAddress?.ilce || null,
                    countryOfBirth: values.countryOfBirth?.il,
                    districtOfBirth: values.districtOfBirth?.ilce,
                    kvkk: 0,
                    birthday: values.birthday ? values.birthday.toISOString() : null,
                };

                const validGuarantors = guarantors.filter(kefil => hasAnyData(kefil));

                const formattedGuarantors = validGuarantors.map(kefil => {
                    // Kefil telefon numaralarını da temizle
                    const cleanKefilPhone1 = kefil.phoneNumber1.replace(/[^\d]/g, '');
                    const kefilPhone1 = cleanKefilPhone1.startsWith('0') ? cleanKefilPhone1.slice(1) : cleanKefilPhone1;

                    const cleanKefilPhone2 = kefil.phoneNumber2 ? kefil.phoneNumber2.replace(/[^\d]/g, '') : null;
                    const kefilPhone2 = cleanKefilPhone2 ? (cleanKefilPhone2.startsWith('0') ? cleanKefilPhone2.slice(1) : cleanKefilPhone2) : null;

                    return {
                        id: kefil.id,
                        name: kefil.name,
                        surname: kefil.surname,
                        identityNumber: kefil.identityNumber,
                        phoneNumber1: kefilPhone1,
                        phoneNumber2: kefilPhone2,
                        countryOfAddress: kefil.countryOfAddress?.il,
                        districtOfAddress: kefil.districtOfAddress?.ilce,
                        detailOfAddress: kefil.detailOfAddress,
                        countryOfWorkAddress: kefil.countryOfWorkAddress?.il || null,
                        districtOfWorkAddress: kefil.districtOfWorkAddress?.ilce || null,
                        detailOfWorkAddress: kefil.detailOfWorkAddress || "",
                        birthday: kefil.birthday ? kefil.birthday.toISOString() : null,
                        countryOfBirth: kefil.countryOfBirth?.il,
                        districtOfBirth: kefil.districtOfBirth?.ilce,
                        isBusinessCustomer: kefil.isBusinessCustomer ? 1 : 0,
                        vkn: kefil.vkn || "",
                        companyName: kefil.companyName || "",
                    };
                });

                const finalPayload = {
                    ...customerPayload,
                    guarantors: formattedGuarantors,
                    deletedGuarantorIds: deletedGuarantorIds
                };

                if (customerData?.id) {
                    await axiosInstance.put(`/api/v1/customers/${customerData.id}`, finalPayload);
                    toast.current.show({
                        severity: "success",
                        summary: "Başarılı",
                        detail: "Müşteri başarılı bir şekilde güncellendi.",
                        life: 3000,
                    });
                } else {
                    await axiosInstance.post('/api/v1/customers/add-customer', finalPayload);
                    toast.current.show({
                        severity: "success",
                        summary: "Başarılı",
                        detail: "Müşteri başarılı bir şekilde eklendi.",
                        life: 3000,
                    });
                }

                resetForm();
                if (onClose) onClose();
            } catch (error) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: customerData ? "Müşteri güncellenirken bir hata oluştu." : "Müşteri eklenirken bir hata oluştu.",
                    life: 3000,
                });
                console.error("Error submitting form:", error);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 1 * 1000);
                setSubmitting(false);
            }
        }
    });

    const handleDelete = async () => {
        setLoading(true); // Global loading başlat
        try {
            if (!customerData?.id) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "Silme işlemi için geçerli bir müşteri ID'si gerekli.",
                    life: 3000,
                });
                return;
            }

            await axiosInstance.delete(`/api/v1/customers/${customerData.id}`);
            toast.current.show({
                severity: "success",
                summary: "Başarılı",
                detail: "Müşteri başarılı bir şekilde silindi.",
                life: 3000,
            });
            if (onClose) onClose();
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Müşteri silinirken bir hata oluştu.",
                life: 3000,
            });
            console.error("Error deleting customer:", error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const { values, errors, touched, handleChange, handleBlur, isSubmitting } = formik;

    const renderCustomerForm = () => (
        <div className="flex flex-col space-y-6 md:space-y-6 overflow-auto p-2 md:p-0">
            {/* Zorunlu alan uyarısı */}
            <div className="flex justify-end">
                <p className="text-red-600 dark:text-red-400 font-semibold italic text-xs md:text-sm">
                    * Zorunlu alanları ifade eder.
                </p>
            </div>
            <WarningArea type='warning' message="Lütfen tüm bilgilerin eksiksiz ve doğru şekilde girildiğinden emin olunuz. Kullanıcı kaynaklı hatalardan doğabilecek maddi kayıplardan PayPlus sorumluluk kabul etmemektedir." />
            {/* İsim Soyisim - Mobilde tek kolon */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
                <InputText
                    camelCase={true}
                    label="İsim"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    clearFunction={() => formik.setFieldValue("name", "")}
                    error={errors.name}
                    touched={touched.name}
                    required
                    className="w-full"
                />
                <InputText
                    camelCase={true}
                    label="Soyisim"
                    name="surname"
                    value={values.surname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    clearFunction={() => formik.setFieldValue("surname", "")}
                    error={errors.surname}
                    touched={touched.surname}
                    required
                    className="w-full"
                />
            </div>

            {/* TC Kimlik No */}
            <InputText
                label="T.C Kimlik No"
                name="identityNumber"
                value={values.identityNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                clearFunction={() => formik.setFieldValue("identityNumber", "")}
                error={errors.identityNumber}
                touched={touched.identityNumber}
                required
                className="w-full"
            />

            {/* Telefon Numarası - Mobilde tek kolon */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
                <InputText
                    type="phone"
                    label="Telefon Numarası"
                    name="phoneNumber1"
                    value={values.phoneNumber1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    clearFunction={() => formik.setFieldValue("phoneNumber1", "")}
                    error={errors.phoneNumber1}
                    touched={touched.phoneNumber1}
                    required
                    className="w-full"
                />
                <InputText
                    type="phone"
                    label="Telefon Numarası - 2"
                    name="phoneNumber2"
                    value={values.phoneNumber2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    clearFunction={() => formik.setFieldValue("phoneNumber2", "")}
                    error={errors.phoneNumber2}
                    touched={touched.phoneNumber2}
                    className="w-full"
                />
            </div>

            {/* Doğum Yeri */}
            <StateSelector
                formik={formik}
                labelIl="Doğum Yeri İl"
                labelIlce="Doğum Yeri İlçe"
                ilFieldName="countryOfBirth"
                ilceFieldName="districtOfBirth"
                required
                isMobile={isMobile}
            />

            {/* Doğum Tarihi
            <FloatLabel>
                <Calendar
                    id="birthday"
                    name="birthday"
                    className={`w-full shadow-sm md:shadow-xl rounded-lg ${touched.birthday && errors.birthday ? 'p-invalid' : ''}`}
                    value={values.birthday}
                    onChange={(e) => formik.setFieldValue("birthday", e.value)}
                    onBlur={handleBlur}
                    dateFormat="dd/mm/yy"
                    locale="tr"
                    touchUI={isMobile}
                    showButtonBar
                    maxDate={new Date()}
                />
                <label htmlFor="birthday" className="ml-2.5">
                    Doğum Tarihi <span className="text-red-500 dark:text-red-400">*</span>
                </label>
            </FloatLabel>
            {touched.birthday && errors.birthday && <small className="p-error text-xs">{errors.birthday}</small>} */}
            <InputText
                type="date"
                label="Doğum Tarihi"
                name="birthday"
                value={values.birthday}
                onChange={handleChange}
                onBlur={handleBlur}
                clearFunction={() => formik.setFieldValue("birthday", "")}
                error={errors.birthday}
                touched={touched.birthday}
            />

            {/* İkametgah Adresi */}
            <StateSelector
                formik={formik}
                labelIl="İkametgah İl"
                labelIlce="İkametgah İlçe"
                ilFieldName="countryOfAddress"
                ilceFieldName="districtOfAddress"
                required
                isMobile={isMobile}
            />
            <WarningArea type='info' message="Lütfen adres detay bilgilerinizi örnekte belirtildiği şekilde eksiksiz ve doğru olarak giriniz. Senet çıktılarınızdada il ve ilçe bilgisi, ilgili seçim alanlarından otomatik olarak alınmaktadır. Örnek adres formatı: Murtaza Mahallesi Hacı Bülbül Sokak Örnek Apartmanı No:13/5" />
            {/* Adres Detayı */}
            <InputText
                type="textarea"
                label="İkamet Adres Detayı"
                name="detailOfAddress"
                value={values.detailOfAddress}
                onChange={handleChange}  // Formik handleChange
                onBlur={handleBlur}
                clearFunction={() => formik.setFieldValue("detailOfAddress", "")}
                error={errors.detailOfAddress}
                touched={touched.detailOfAddress}
                required
                rows={3}
                className="w-full"
                camelCase={true}
            />

            {/* Ticari Müşteri CheckBox */}
            <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Checkbox
                    inputId="isBusinessCustomer"
                    className="shadow-sm md:shadow-xl rounded-lg border-2"
                    checked={values.isBusinessCustomer}
                    onChange={(e) => {
                        formik.setFieldValue("isBusinessCustomer", e.checked);
                        if (!e.checked) {
                            formik.setFieldValue("vkn", "");
                            formik.setFieldValue("companyName", "");
                            formik.setFieldValue("countryOfWorkAddress", null);
                            formik.setFieldValue("districtOfWorkAddress", null);
                            formik.setFieldValue("detailOfWorkAddress", "");
                        }
                    }}
                />
                <label htmlFor="isBusinessCustomer" className="ml-2 dark:text-gray-200 text-sm md:text-base">
                    Ticari müşteri mi?
                </label>
            </div>

            {/* Ticari Bilgiler */}
            {values.isBusinessCustomer && (
                <div className="space-y-6 md:space-y-6 px-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <InputText
                        label="VKN"
                        name="vkn"
                        value={values.vkn}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        clearFunction={() => formik.setFieldValue("vkn", "")}
                        error={errors.vkn}
                        touched={touched.vkn}
                        required
                        className="w-full"
                    />
                    <InputText
                        camelCase={true}
                        label="Firma Ünvanı"
                        name="companyName"
                        value={values.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        clearFunction={() => formik.setFieldValue("companyName", "")}
                        error={errors.companyName}
                        touched={touched.companyName}
                        required
                        className="w-full"
                    />

                    {/* İş Yeri Adresi */}
                    <StateSelector
                        formik={formik}
                        labelIl="İş Yeri İl"
                        labelIlce="İş Yeri İlçe"
                        ilFieldName="countryOfWorkAddress"
                        ilceFieldName="districtOfWorkAddress"
                        required
                        isMobile={isMobile}
                    />
                    <WarningArea type='info' message="Lütfen adres detay bilgilerinizi örnekte belirtildiği şekilde eksiksiz ve doğru olarak giriniz. Senet çıktılarınızdada il ve ilçe bilgisi, ilgili seçim alanlarından otomatik olarak alınmaktadır. Örnek adres formatı: Murtaza Mahallesi Hacı Bülbül Sokak Örnek Apartmanı No:13/5" />
                    <InputText
                        camelCase={true}
                        type="textarea"
                        label="İş Yeri Adres Detayı"
                        name="detailOfWorkAddress"
                        value={values.detailOfWorkAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        clearFunction={() => formik.setFieldValue("detailOfWorkAddress", "")}
                        error={errors.detailOfWorkAddress}
                        touched={touched.detailOfWorkAddress}
                        required
                        rows={3}
                        className="w-full"
                    />
                </div>
            )}
        </div>
    );

    const toggleGuarantorPanel = () => {
        setShowGuarantorPanel(!showGuarantorPanel);
    };

    useEffect(() => {
        // showGuarantorPanel durumu değiştiğinde parent component'e bildir
        if (onKefilPanelToggle) {
            onKefilPanelToggle(showGuarantorPanel);
        }
    }, [showGuarantorPanel, onKefilPanelToggle]);

    return (
        <div className="w-full h-full flex flex-col">
            <Toast ref={toast} position={isMobile ? "bottom-center" : "top-right"} />

            {/* Ana Form Container */}
            <form className="flex-1 flex flex-col overflow-hidden">
                {/* Mobilde Tab View, Desktop'ta yan yana panel */}
                {isMobile ? (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Mobil Tab Butonları */}
                        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                            <button
                                type="button"
                                onClick={() => setShowGuarantorPanel(false)}
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${!showGuarantorPanel
                                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-900'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                            >
                                <i className="pi pi-user mr-2"></i>
                                Müşteri
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowGuarantorPanel(true)}
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${showGuarantorPanel
                                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white dark:bg-gray-900'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                            >
                                <i className="pi pi-users mr-2"></i>
                                Kefiller
                            </button>
                        </div>

                        {/* Mobil İçerik */}
                        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
                            {!showGuarantorPanel ? renderCustomerForm() : (
                                <GuarantorForm
                                    initialGuarantors={guarantors}
                                    onGuarantorsChange={handleGuarantorsChange}
                                    guarantorValidationSchema={guarantorValidationSchema}
                                    toastRef={toast}
                                    isMobile={isMobile}
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    /* Desktop Görünüm */
                    <div className="flex-1 flex gap-4 overflow-hidden">
                        {/* Müşteri Bilgileri Paneli */}
                        <div className={`bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg overflow-y-auto transition-all duration-300 ${showGuarantorPanel ? 'w-1/2' : 'w-full'
                            }`}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold dark:text-gray-200">
                                    Müşteri Bilgileri
                                </h3>
                                <Button
                                    type="button"
                                    label={showGuarantorPanel ? "Kefil Panelini Gizle" : "Kefil Panelini Göster"}
                                    icon={showGuarantorPanel ? "pi pi-angle-double-left" : "pi pi-angle-double-right"}
                                    className="rounded-xl
                                    shadow-md
                                    cursor-pointer
                                    border
                                    bg-transparent
                                    text-gray-700
                                    dark:text-white
                                    border-gray-700
                                    hover:border-gray-500
                                    hover:bg-gray-500
                                    hover:text-white
                                    dark:bg-gray-600 
                                    dark:border-gray-600
                                    dark:hover:bg-gray-700
                                    dark:hover:border-gray-700
                                    dark:shadow-lg
                                    p-2
                                    transition-all
                                    duration-200"
                                    onClick={toggleGuarantorPanel}
                                />
                            </div>
                            {renderCustomerForm()}
                        </div>

                        {/* Kefil Bilgileri Paneli */}
                        {showGuarantorPanel && (
                            <div className="w-1/2 bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg overflow-y-auto">
                                <GuarantorForm
                                    initialGuarantors={guarantors}
                                    onGuarantorsChange={handleGuarantorsChange}
                                    guarantorValidationSchema={guarantorValidationSchema}
                                    toastRef={toast}
                                    isMobile={isMobile}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Alt Butonlar */}
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
                    <div className={`flex ${isMobile ? 'flex-col-reverse' : 'flex-row justify-end'} gap-3`}>
                        <Button
                            type="button"
                            label="İptal"
                            icon="pi pi-times"
                            onClick={() => {
                                formik.resetForm();
                                if (onClose) onClose();
                            }}
                            className="rounded-xl shadow-lg cursor-pointer text-white bg-red-700 dark:bg-red-800 hover:bg-red-800 dark:hover:bg-red-700 p-2"

                        />

                        {customerData && (
                            <Button
                                type="button"
                                label="Sil"
                                icon="pi pi-trash"
                                onClick={() => showDialog({
                                    message: "Müşteri verisini silmek istediğinize emin misiniz?",
                                    onConfirm: () => handleDelete()
                                })}
                                className="rounded-xl shadow-lg cursor-pointer text-white bg-red-700 dark:bg-red-800 hover:bg-red-800 dark:hover:bg-red-700 p-2"

                            />
                        )}
                        <Button
                            type="button"
                            label={customerData ? "Güncelle" : "Kaydet"}
                            icon={customerData ? "pi pi-refresh" : "pi pi-save"}
                            loading={isSubmitting}
                            disabled={isSubmitting}
                            onClick={() => {
                                showDialog({
                                    message: customerData
                                        ? "Müşteri verisini güncellemek istediğinize emin misiniz?"
                                        : "Müşteriyi kaydetmek istediğinize emin misiniz?",
                                    onConfirm: () => formik.submitForm()
                                });
                            }}
                            className={`rounded-xl shadow-lg cursor-pointer text-white ${customerData
                                ? 'bg-orange-500 dark:bg-orange-600 hover:bg-orange-600 dark:hover:bg-orange-500'
                                : 'bg-green-700 dark:bg-green-800 hover:bg-green-800 dark:hover:bg-green-700'
                                } p-2`}
                        />
                    </div>
                </div>
            </form>

            <style jsx>{`
                /* Dark mode için özel stiller */
                :global(.dark .p-inputtext),
                :global(.dark .p-dropdown),
                :global(.dark .p-calendar .p-inputtext),
                :global(.dark .p-inputtextarea) {
                    background-color: #1f2937;
                    border-color: #374151;
                    color: #f3f4f6;
                }

                :global(.dark .p-inputtext:enabled:focus),
                :global(.dark .p-dropdown:not(.p-disabled):hover),
                :global(.dark .p-calendar .p-inputtext:enabled:focus) {
                    border-color: #60a5fa;
                }

                :global(.dark .p-float-label label) {
                    color: #9ca3af;
                }

                :global(.dark .p-checkbox .p-checkbox-box) {
                    background-color: #1f2937;
                    border-color: #374151;
                }

                :global(.dark .p-checkbox .p-checkbox-box.p-highlight) {
                    background-color: #2563eb;
                    border-color: #2563eb;
                }

                /* Mobil için touch-friendly boyutlar */
                @media (max-width: 768px) {
                    :global(.p-inputtext),
                    :global(.p-dropdown),
                    :global(.p-calendar .p-inputtext) {
                        min-height: 44px;
                        font-size: 16px;
                    }

                    :global(.p-button) {
                        min-height: 44px;
                    }

                    :global(.p-checkbox .p-checkbox-box) {
                        width: 24px;
                        height: 24px;
                    }
                }

                /* Scroll bar stilleri */
                :global(.dark ::-webkit-scrollbar) {
                    width: 8px;
                    height: 8px;
                }

                :global(.dark ::-webkit-scrollbar-track) {
                    background: #1f2937;
                }

                :global(.dark ::-webkit-scrollbar-thumb) {
                    background: #4b5563;
                    border-radius: 4px;
                }

                :global(.dark ::-webkit-scrollbar-thumb:hover) {
                    background: #6b7280;
                }
            `}</style>
        </div>
    );
};

AddCustomer.propTypes = {
    onClose: PropTypes.func.isRequired,
    customerData: PropTypes.object,
    onKefilPanelToggle: PropTypes.func
};

export default AddCustomer;